import React from 'react';
import { LockIcon, ShieldCheckIcon, StarIcon, ZapIcon } from 'lucide-react';
import { costComparisons, systemMockupUrl } from '../data/content';
import { useCountdown } from '../hooks/useCountdown';
import { CtaButton } from './CtaButton';
function CountdownUnit({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="w-20 rounded-xl border border-white/15 bg-white/5 px-2 py-3 text-center sm:w-24" data-id="element-135" data-mp="1hpqqlpl36aw0">
      <p className="text-3xl font-extrabold tabular-nums tracking-tight text-slate-50 sm:text-4xl" data-id="element-136" data-mp="tzmstyukx5cx">
        {value}
      </p>
      <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400" data-id="element-137" data-mp="p1v0x4txpj8q">
        {label}
      </p>
    </div>;
}
export function Offer() {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown(3 * 60 * 60 + 47 * 60 + 12);
  return <section id="offer" className="w-full bg-navy-deep py-16 sm:py-24" aria-labelledby="offer-title" data-id="element-138" data-mp="2fbemm9dmqr70">
      <div className="mx-auto max-w-3xl px-6" data-id="element-139" data-mp="2ac1sd3kfihue">
        <h2 id="offer-title" className="text-center text-3xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-4xl" data-id="element-140" data-mp="2affuqbt3er1u">
          The Cost of Not Having a System Is Always Higher Than $27.
        </h2>

        <ul className="mt-10 space-y-3" data-id="element-141" data-mp="zi53e6zmge10">
          {costComparisons.map(item => <li key={item.label} className="flex flex-col gap-1 rounded-xl border border-white/10 bg-white/[0.04] p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6" data-id="element-142" data-mp="13ki60mj2nvtk">
              <span className="text-slate-300" data-id="element-143" data-mp="btvkpdxpg6e3">{item.label}:</span>
              <span className="text-lg font-extrabold text-red-300" data-id="element-144" data-mp="funx2g1virel">{item.value}</span>
            </li>)}
          <li className="flex flex-col gap-1 rounded-xl border border-trust/40 bg-trust/10 p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6" data-id="element-145" data-mp="klnpb801slar">
            <span className="font-semibold text-slate-50" data-id="element-146" data-mp="3k6jegk6vqs2l">The Pharma-Masterclave:</span>
            <span className="text-lg font-extrabold text-trust" data-id="element-147" data-mp="3o8edtj592vdq">$27. One time. Yours forever.</span>
          </li>
        </ul>

        <div className="mt-12 rounded-3xl border border-white/10 bg-white/[0.04] p-8 shadow-2xl sm:p-12" data-id="element-148" data-mp="qgnqgd7e0yi4">
          <div className="text-center" data-id="element-149" data-mp="155k54t3y99rp">
            <p className="text-xl font-extrabold uppercase tracking-wide text-slate-50" data-id="element-150" data-mp="1548se3sj0rog">
              The Complete System
            </p>
            <p className="mt-2 text-sm font-semibold text-trust" data-id="element-151" data-mp="108tgvihzr7v7">
              6 Complete Guides + 3 Action Tools
            </p>
          </div>

          <img src={systemMockupUrl} alt="Everything included: the 6 complete guides and 3 printable action tools" className="mt-8 w-full rounded-2xl border border-white/10 bg-white shadow-xl" loading="lazy" style={{
          height: "177px"
        }} data-id="element-152" data-mp="7il4k7a4zhoj" />

          <div className="mt-8 flex flex-col items-center" data-id="element-153" data-mp="heobdqfxo4ht">
            <p className="text-base text-slate-400" data-id="element-154" data-mp="1wvooqpjvxsrv">
              Regular Price: <s className="decoration-red-400 decoration-2" data-id="element-155" data-mp="1svejxg7tx8el">$197</s>
            </p>
            <p className="mt-2 text-sm font-semibold uppercase tracking-[0.16em] text-slate-300" data-id="element-156" data-mp="1sxh5no1y8fdi">
              Today Only
            </p>
            <p className="mt-1 text-6xl font-extrabold tracking-tight text-slate-50 sm:text-7xl" data-id="element-157" data-mp="25e0e7onnc43l">
              $27
            </p>

            <div id="checkout" className="mt-8 w-full scroll-mt-24" data-id="element-158" data-mp="1qi111zlojdrl">
              <CtaButton href="#final-cta" className="w-full" data-id="element-159" data-mp="1vglyxsdgllcv">
                ✅ Yes, I want the complete system — $27
              </CtaButton>
            </div>

            <p className="mt-4 flex items-center gap-2 text-sm text-slate-400" data-id="element-160" data-mp="2o48c9scho4cx">
              <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-161" data-mp="2o6az63ichhdf" />
              One-time payment. Instant digital delivery. Yours forever.
            </p>
          </div>

          <div className="mt-10 border-t border-white/10 pt-8" data-id="element-162" data-mp="2ed3wwriwihhk">
            <p className="text-center text-sm font-bold uppercase tracking-[0.14em] text-red-300" data-id="element-163" data-mp="323hibz6zdi36">
              This launch price will not last. Lock it in before it goes up.
            </p>
            <div className="mt-5 flex items-center justify-center gap-3" role="timer" aria-live="off" aria-label={`Offer ends in ${hours} hours, ${minutes} minutes and ${seconds} seconds`} data-id="element-164" data-mp="1j64qz61vl1vr">
              <CountdownUnit value={hours} label="Hours" data-id="element-165" data-mp="198gsq6529c8u" />
              <CountdownUnit value={minutes} label="Minutes" data-id="element-166" data-mp="149x8ca6488il" />
              <CountdownUnit value={seconds} label="Seconds" data-id="element-167" data-mp="21ww6kpcghoil" />
            </div>
          </div>

          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-slate-300" data-id="element-168" data-mp="21yqg77ia1u7d">
            <li className="flex items-center gap-2" data-id="element-169" data-mp="1s6t3fwbmeu6j">
              <span className="flex items-center gap-0.5 text-trust" aria-hidden="true" data-id="element-170" data-mp="1s5hxbpupdac9">
                {[0, 1, 2, 3, 4].map(star => <StarIcon key={star} className="h-4 w-4 fill-current" data-id="element-171" data-mp="2kn3sjuo0xybk" />)}
              </span>
              <span className="font-semibold text-slate-50" data-id="element-172" data-mp="2fw5bgsv7ow79">4.9/5 Verified Reviews</span>
            </li>
            <li className="flex items-center gap-2" data-id="element-173" data-mp="2o5t9gxmwjhkt">
              <ShieldCheckIcon className="h-4 w-4 text-trust" aria-hidden="true" data-id="element-174" data-mp="2o7e2gitkisgk" />
              7-Day Money-Back Guarantee
            </li>
            <li className="flex items-center gap-2" data-id="element-175" data-mp="2x5g7rakc479n">
              <ZapIcon className="h-4 w-4 text-trust" aria-hidden="true" data-id="element-176" data-mp="2x82fpbb50ose" />
              Instant Delivery
            </li>
          </ul>
        </div>
      </div>
    </section>;
}